import React, { useCallback, useState } from 'react';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';

import AlertSuccess from '@rover/icons/svg/alert-success.svg';
import { Box, Button, Flex } from '@rover/kibble/core';
import TextWithSingleInlineLink from '@rover/kibble/official-patterns/TextWithSingleInlineLink';
import HTMLText from '@rover/kibble/patterns/HTMLText';
import useTheme from '@rover/kibble/themes/useTheme';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { getParseCancellationDays } from '../../../../../utils/getParseCancellationDays';
import ResponsiveOverlay from '../ResponsiveOverlay/ResponsiveOverlay';

import { SitterProfileCancellationPolicyClicked } from './analyticsEvents';

type Props = {
  cancellationPolicy: string | undefined;
  serviceName: string | undefined;
  cancellationPolicyDesc: string | undefined;
  providerId: string;
  onCancelationPolicyClick?: (cancelationPolicyDesc: string) => void;
};

const RevampCancellationPolicy = ({
  cancellationPolicy,
  serviceName,
  cancellationPolicyDesc,
  providerId,
  onCancelationPolicyClick,
}: Props): JSX.Element | null => {
  const { t } = useLingui();
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = useCallback(() => setIsModalOpen(true), []);
  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  if (!cancellationPolicy) return null;

  const days = getParseCancellationDays(cancellationPolicy);
  if (days === undefined) return null;

  const daysText = t({
    message: plural(days, { one: '# day', other: '# days' }),
  });
  const hasModal = !!cancellationPolicyDesc && !onCancelationPolicyClick;
  const modalDesc = serviceName
    ? `<strong>${serviceName}: </strong> ${cancellationPolicyDesc}`
    : cancellationPolicyDesc ?? '';

  const handleClick = (): void => {
    emitAnalyticsEvent(
      new SitterProfileCancellationPolicyClicked({
        providerId,
        pageVariant: 'revamp',
      })
    );
    if (hasModal) {
      handleOpenModal();
    } else if (onCancelationPolicyClick && modalDesc) {
      onCancelationPolicyClick(modalDesc);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const linkText =
    days === 0
      ? t`Cancel <0>before</0> the booking begins.`
      : t`Cancel up to <0>${daysText}</0> before.`;

  const a11yLabel = linkText.replace(/<\/?0>/g, '');

  return (
    <>
      <Flex
        data-testid="revamp-cancellation-policy"
        justifyContent="center"
        alignItems="center"
        gap="2x"
      >
        <Flex flexShrink="0" width="16px" height="16px" alignItems="center" justifyContent="center">
          <AlertSuccess
            width="16"
            height="16"
            aria-hidden="true"
            fill={theme.textColor.success?.toString()}
          />
        </Flex>
        <Box
          role="button"
          onPress={handleClick}
          sx={{ cursor: 'pointer' }}
          webProps={{
            tabIndex: 0,
            'aria-label': a11yLabel,
            onKeyDown: handleKeyDown,
          }}
        >
          <Box webProps={{ 'aria-hidden': true }}>
            <TextWithSingleInlineLink paragraphText={linkText} size="100" />
          </Box>
        </Box>
      </Flex>

      <ResponsiveOverlay
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        title={t`Cancellation Policy`}
      >
        <Flex flexDirection="column" gap="4x">
          <HTMLText text={modalDesc} />
          <Button fullWidth onPress={handleCloseModal}>
            <Trans>Close</Trans>
          </Button>
        </Flex>
      </ResponsiveOverlay>
    </>
  );
};

export default RevampCancellationPolicy;
